import {createGrid} from "../../../helpers/grids/grids";
import {createCellRenderer} from "../../../helpers/grids/helpers";
import createElement from "../../../helpers/createElement";
import {waitUntil} from "../../../helpers/waitUntil";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import handleAjaxError from "../../../helpers/handleAjaxError";
import {handleAjaxSuccess} from "../../../helpers/swalHelper";

function initProductionProcessCrewRequestsIndex() {

	// A list of rows that have been updated, keyed by their public id
	/** @var {Object<string,ProductionProcessCrewIndexDefs.UpdatedRowCacheEntry>} */
	const updatedRows = {};

	const grid = createGrid('.process-crew-requests-grid', {
			columnDefs: [
				{
					field: "full_name",
					headerName: 'Name',
					sortable: true,
					pinned: 'left',
					cellRenderer: createCellRenderer(params => {
						/** @var {Object<string,any>} */
						const request = params.data;
						return createElement(`
							<div style="line-height: 1">
								<div>${params.data.full_name}</div>
								<div class="small text-muted text-wrap py-1" title="Audition Type">${request.crew_type_string}</div>
							</div>
						`);
					}),
					minWidth: 200,
					suppressAutoSize: true,
					autoHeight: true,
					sort: 'asc',
				},
				{
					field: 'Add To Production',
					cellClass: 'fill-space',
					maxWidth: 220,
					cellRenderer: createCellRenderer(params => {
						/** @var {Object<string,any>} */
						const row = params.data;
						// Retrieve initial value
						let isAcceptedFlagValue = row.is_accepted ? row.is_accepted : '';

						// Use locally cached value
						if (updatedRows[row.public_id]) {
							// noinspection JSUnresolvedReference
							isAcceptedFlagValue = updatedRows[row.public_id].is_accepted;
						}

						const cell = createElement(`
							<div class="form-group d-flex justify-content-center align-items-center my-0 h-100">
								<input
									id="is_accepted_${row.public_id}"
									style="width: 20px"
									type="checkbox"
									name="is_accepted"
									class="form-control is_accepted_flag_input"
									placeholder="Not Set"
									autocomplete="off"
									${isAcceptedFlagValue ? 'checked' : ''}
								>
							</div>
						`)

						const input = cell.querySelector('input');

						input.addEventListener('change', () => {
							handleIsAcceptedFlagChange(params.data, input.checked)
						})

						return cell;
					}),
					suppressKeyboardEvent: () => {
						// Disable default tab behavior
						return true;
					}
				},
			],
			getRowClass: params => {
				if (hasRowBeenUpdated(params.data)) {
					return 'updated';
				}

				return '';
			},
			paginationPageSize: 500,
			cacheBlockSize: 500,
			_rowName: 'Crew',
		},
		window['routes/admin.productions.process-crew-requests.grid']
	);

	// Setup submit changes button
	const submitChangesButton = document.querySelector('.submit-button');
	if (submitChangesButton) {
		submitChangesButton.addEventListener('click', () => {
			submitChanges(submitChangesButton.dataset.route)
		})
	}

	/** Methods */

	/**
	 * Initializes
	 *
	 * @param row
	 * @return {ProductionProcessCrewIndexDefs.UpdatedRowCacheEntry}
	 */
	function getUpatedRowCacheEntry(row) {
		if (!updatedRows[row.public_id]) {
			updatedRows[row.public_id] = {
				public_id: row.public_id,
				have_paperwork: row.have_paperwork,
				is_accepted: row.is_accepted,
				stipend: row.stipend ? parseFloat(row.stipend) : '',
			};
		}

		return updatedRows[row.public_id];
	}

	function handleIsAcceptedFlagChange(row, value) {
		const updatedRowEntry = getUpatedRowCacheEntry(row);

		updatedRowEntry.is_accepted = value;

		refreshGridRows(row);
	}

	// noinspection DuplicatedCode
	function refreshGridRows(rowData = undefined) {
		// Wait until the next element has focus
		waitUntil(() => document.querySelector(':focus'))
			.then(() => {
				// Then save which element it was...
				const previouslyFocusedElement = document.querySelector(':focus');

				if (rowData) {
					// ... redraw only given row ...
					const node = getRelatedRowNode(rowData);
					grid.api.redrawRows({
						rowNodes: [node],
					});
				} else {
					// ... redraw all rows ...
					grid.api.redrawRows();
				}

				// ... and wait until said element is back into the dom ...
				waitUntil(
					() => {
						return document.getElementById(previouslyFocusedElement.id)
					},
					{timeoutAfter: 2000}
				).then(() => {
					// ... then focus it
					document.getElementById(previouslyFocusedElement.id).focus();
				})

				// Also update change counter
				document.querySelector('.changes-counter').innerHTML = ` (${getChangedRows().length})`;
			})
	}

	function getChangedRows() {
		const changedRows = [];
		grid.api.forEachNode(node => {
			if (hasRowBeenUpdated(node.data)) {
				changedRows.push(updatedRows[node.data.public_id]);
			}
		})

		return changedRows;
	}

	function hasRowBeenUpdated(row) {
		const updatedRowCacheRecord = updatedRows[row.public_id];

		// If we don't even have a cache record, it has not been updated
		if (!updatedRowCacheRecord) {
			return false;
		}

		// Original server processed by accounting flag
		const isAccepted = row.is_accepted;

		return (updatedRowCacheRecord.is_accepted !== isAccepted) // if flag was changed
	}

	// noinspection DuplicatedCode
	function submitChanges(route) {
		if (getChangedRows().length === 0) {
			// Do nothing
			return;
		}

		loading()

		Axios.post(route, {
			rows: getChangedRows(),
		})
			.then(handleAjaxSuccess)
			.catch(handleAjaxError)
			.finally(stopLoading);
	}

	/**
	 * @param rowData
	 * @return {import('ag-grid-community/dist/lib/interfaces/iRowNode').IRowNode}
	 */
	function getRelatedRowNode(rowData) {
		let relatedNode = null;
		grid.api.forEachNode(node => {
			if (relatedNode) {
				return;
			}

			if (node.data === rowData) {
				relatedNode = node;
			}
		})

		return relatedNode;
	}
}

export {
	initProductionProcessCrewRequestsIndex
}
